import { graphql } from 'gatsby';
import { useEffect, useState } from 'react';

import { SEO } from 'components';
import { PageScrollProgressBar } from 'components/PageScrollProgressBar';
import Article from 'components/pages/learn/article/Article';
import { Layout } from 'components/rebrand/Layout';

import { TAfAdsets } from 'typescriptDefs/afTracking';
import { IArticleProps, IArticleSanityData, IQueryParamsGROQ } from 'typescriptDefs/pages/learn/article';

import { useGtagRemarketing } from 'helpers';
import { useScrollY } from 'helpers/useScrollDepth';

const ArticleLayout = ({
  data: { article, logoKOHO, pageLearn },
  location,
  pageContext: { locale, pageIsMultilingual, pageName, schemaMarkupJson },
}: IArticleProps) => {
  useGtagRemarketing('JOxeCPXq4JYDEI-zyK4D');

  const [articleInView, setArticleInView] = useState<IArticleSanityData>(article);

  const [articles, setArticles] = useState<IArticleSanityData[]>([article]);

  const [queryParamsGROQ, setQueryParamsGROQ] = useState<IQueryParamsGROQ>({
    _type: 'blogArticle',
    idsDraft: [`drafts.${article._id.replace(/^drafts\./, '')}`],
    idsPublished: [article._id.replace(/^drafts\./, '')],
    locale,
    tags: article.tags,
    titles: [article.title],
  });

  const scrollY = useScrollY({ trackScrollDepth: true });
  const [hasScrolled, setHasScrolled] = useState<boolean>(false);

  useEffect(() => {
    window.addEventListener('scroll', () => setHasScrolled(true), {
      once: true,
    });
  }, []);

  const canonicalLink = `${location.origin || process.env.GATSBY_REPO_URL}${
    { en: '/', fr: '/fr/' }[locale]
  }${articleInView.subfolder ?? 'learn'}/${articleInView.slug.current}/`;

  useEffect(() => history.pushState(null, '', canonicalLink), [canonicalLink]);

  return (
    <Layout
      afAdset={articleInView._id.split('-')[0] as TAfAdsets}
      location={location}
      pageIsMultilingual={pageIsMultilingual}
      pageName={pageName}
      pageVariant={articleInView.title}
      trackScrollDepth={false}
    >
      <SEO
        canonical={
          articleInView.canonicalLink && articleInView.canonicalLink.length > 0
            ? articleInView.canonicalLink
            : canonicalLink
        }
        description={articleInView.metadataDescription}
        imageSrc={articleInView.img.url || articleInView.img.asset.gatsbyImageData.images?.fallback?.src}
        keywords={articleInView.metadataKeywords}
        location={location}
        multilingual={pageIsMultilingual}
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': canonicalLink,
            },
            headline: articleInView.title,
            description: articleInView.metadataDescription,
            image: articleInView.img.url || articleInView.img.asset.gatsbyImageData.images?.fallback?.src,
            author: {
              '@type': 'Person',
              name: articleInView.authorData.name,
              url: `${location.origin}${
                { en: '/', fr: '/fr/' }[locale]
              }author/${articleInView.authorData.slug.current}/`,
            },
            publisher: {
              '@type': 'Organization',
              name: 'KOHO',
              logo: {
                '@type': 'ImageObject',
                url: `${location.origin}${logoKOHO.publicURL}`,
              },
            },
            datePublished: articleInView._createdAt,
            dateModified: articleInView._updatedAt,
          },
          ...(schemaMarkupJson || []),
        ]}
        title={articleInView.metadataTitle}
      />
      <PageScrollProgressBar className="top-navMobileHeight lg:top-navHeight" />

      {articles.map((article, i) => (
        <Article
          article={article}
          articles={articles}
          blogArticleDisclaimer={pageLearn.blogArticleDisclaimer}
          hasScrolled={hasScrolled}
          isGROQ={i !== 0}
          key={i}
          pathname={location.pathname}
          queryParamsGROQ={queryParamsGROQ}
          setArticleInView={setArticleInView}
          setArticles={setArticles}
          setQueryParamsGROQ={setQueryParamsGROQ}
          scrollY={scrollY}
        />
      ))}
    </Layout>
  );
};

export default ArticleLayout;

export const queryGraphQL = graphql`
  query ($_id: String!, $locale: String!) {
    article: sanityBlogArticle(_id: { eq: $_id }) {
      _createdAt
      _id
      _rawBody
      _rawExcerpt
      _updatedAt
      authorData: author {
        _id
        bio
        img {
          asset {
            _id
            gatsbyImageData(placeholder: NONE, width: 200, height: 200)
          }
        }
        locale
        name
        slug {
          current
        }
      }
      canonicalLink
      img {
        asset {
          _id
          gatsbyImageData(placeholder: NONE, width: 1376, height: 874)
          url
        }
      }
      imgAltText
      metadataDescription
      metadataKeywords
      metadataTitle
      minutesToRead
      removeDisclaimer
      reviewerData: reviewer {
        _id
        img {
          asset {
            _id
            gatsbyImageData(placeholder: NONE, width: 200, height: 200)
          }
        }
        name
        slug {
          current
        }
      }
      slug {
        current
      }
      sources {
        link
        source
      }
      subfolder
      summary
      tags
      title
    }
    logoKOHO: file(relativePath: { eq: "logos/koho/main.png" }) {
      publicURL
    }
    pageLearn: sanityPageLearn(locale: { eq: $locale }) {
      blogArticleDisclaimer
    }
  }
`;
